/**
 * Monetizzazione Premium — hotel, B&B e negozi Plus (Profilo Base vs Plus Premium).
 *
 * DATI RISERVATI in MongoDB `nodi_riservati` (endpoint /api/account/nodi-riservati):
 * camereDisponibili, isPremium, premiumData (prezzi/inventario/carrello),
 * certificazioneOspitalita (voti, giudizi, recensioni), overlay negoziPremium.
 *
 * Nel bundle pubblico resta solo la directory pubblica: nome, tipo, coordinate,
 * link prenotazione (usati dai marker della mappa globale).
 */

import { GOVERNANCE_PRIMARY_SLUGS } from "./governanceSlugs";
import { applyAdminOspitalitaOverlays } from "./certificatoAdminData";

export const PREMIUM_SLUGS = [
  "campli",
  "mosciano-sant-angelo",
  "castelli",
  "castiglione-messer-raimondo",
];

/**
 * Seed per comune — solo campi pubblici (directory strutture sulla mappa).
 * I record completi (camere, piano Premium, certificazione) arrivano da
 * `reserved.strutture_ospitalita` dopo autenticazione.
 */
export const PREMIUM_META = {
  campli: {
    struttureOspitalita: [
      {
        nome: "Albergo Diffuso Palazzo dei Priori",
        tipo: "Albergo Diffuso",
        coords: { lat: 42.702, lng: 13.691 },
        linkPrenotazione: "/richiedi-accesso?tipo=hotel&borgo=campli",
      },
      {
        nome: "B&B Via del Borgo",
        tipo: "B&B",
        coords: { lat: 42.6985, lng: 13.689 },
        linkPrenotazione: "/host?borgo=campli",
      },
    ],
  },
  "mosciano-sant-angelo": {
    struttureOspitalita: [
      {
        nome: "Hotel Il Fino",
        tipo: "Hotel",
        coords: { lat: 42.7499, lng: 13.8895 },
        linkPrenotazione: "/richiedi-accesso?tipo=hotel&borgo=mosciano-sant-angelo",
      },
      {
        nome: "B&B Colle Sant'Angelo",
        tipo: "B&B",
        coords: { lat: 42.7464, lng: 13.8875 },
        linkPrenotazione: "/host?borgo=mosciano-sant-angelo",
      },
    ],
  },
  castelli: {
    struttureOspitalita: [
      {
        nome: "Agriturismo Ceramica e Montagna",
        tipo: "Agriturismo",
        coords: { lat: 42.5048, lng: 13.7038 },
        linkPrenotazione: "/richiedi-accesso?tipo=agriturismo&borgo=castelli",
      },
      {
        nome: "B&B Bottega del Vento",
        tipo: "B&B",
        coords: { lat: 42.5013, lng: 13.7018 },
        linkPrenotazione: "/host?borgo=castelli",
      },
    ],
  },
  "castiglione-messer-raimondo": {
    struttureOspitalita: [
      {
        nome: "Hotel Valle del Fino",
        tipo: "Hotel",
        coords: { lat: 42.5575, lng: 13.8898 },
        linkPrenotazione: "/richiedi-accesso?tipo=hotel&borgo=castiglione-messer-raimondo",
      },
      {
        nome: "Agriturismo della Valle",
        tipo: "Agriturismo",
        coords: { lat: 42.554, lng: 13.8878 },
        linkPrenotazione: "/host?borgo=castiglione-messer-raimondo",
      },
    ],
  },
  napoli: {
    struttureOspitalita: [
      {
        nome: "Grand Hotel Oriente",
        tipo: "Hotel",
        coords: { lat: 40.8538, lng: 14.2691 },
        linkPrenotazione: "/comune/napoli#tab-dormire",
      },
      {
        nome: "L'Alloggio dei Vassalli",
        tipo: "B&B",
        coords: { lat: 40.8503, lng: 14.2671 },
        linkPrenotazione: "/comune/napoli#tab-dormire",
      },
    ],
  },
};

/** KPI aggregati profili Premium (cruscotti) */
export const PREMIUM_NETWORK_KPI = [
  { id: "strutture", label: "Strutture ricettive", value: 8 },
  { id: "premium", label: "Profili Premium attivi", value: 6 },
];

/**
 * Applica flag Premium ai negozi della rete commerciale.
 * @param {import('../components/borgo/borgoTypes').NegozioTipico[]} negozi
 * @param {Record<string, { isPremium: boolean, premiumData?: object }>} [overrides]
 */
export function applyNegoziPremium(negozi, overrides = {}) {
  return (negozi || []).map((n) => {
    const ov = overrides[n.nome];
    if (ov) {
      return {
        ...n,
        isPremium: ov.isPremium,
        ...(ov.premiumData ? { premiumData: ov.premiumData } : {}),
        ...(ov.certificazioneOspitalita ? { certificazioneOspitalita: ov.certificazioneOspitalita } : {}),
      };
    }
    if (typeof n.isPremium === "boolean") return n;
    return { ...n, isPremium: false };
  });
}

/**
 * @param {string} slug
 * @param {object} [raw]
 * @param {{ reteNegozi?: object[] }} [commerce]
 * @param {object} [reserved] — doc `nodi_riservati` (strutture/negozi completi)
 */
export function mergePremiumFields(slug, raw, commerce = {}, reserved) {
  const meta = PREMIUM_META[slug] || {};
  const fromJson = raw?.strutture_ospitalita ?? raw?.struttureOspitalita;
  const struttureOspitalita = reserved?.strutture_ospitalita?.length
    ? reserved.strutture_ospitalita
    : fromJson?.length
      ? fromJson
      : meta.struttureOspitalita ?? [];

  // Negozi già completi (premiumData inclusi) se il cruscotto ha passato `reserved`.
  const reteNegozi = commerce.reteNegozi ?? [];

  const premiumCount =
    reteNegozi.filter((n) => n.isPremium).length +
    struttureOspitalita.filter((s) => s.isPremium).length;

  const withOverlays = applyAdminOspitalitaOverlays(struttureOspitalita, reteNegozi);

  return {
    struttureOspitalita: withOverlays.struttureOspitalita,
    strutture_ospitalita: withOverlays.struttureOspitalita,
    reteNegozi: withOverlays.reteNegozi,
    rete_commerciale: {
      ...(commerce.rete_commerciale || {}),
      rete_negozi: withOverlays.reteNegozi,
    },
    premiumStats: {
      struttureCount: struttureOspitalita.length,
      premiumCount,
    },
  };
}

/** @param {import('../components/borgo/borgoTypes').StrutturaRicettiva[]} strutture */
export function sortStrutture(strutture) {
  const tipoOrder = { Hotel: 0, "Albergo Diffuso": 1, Agriturismo: 2, "B&B": 3 };
  return [...(strutture || [])].sort(
    (a, b) => (tipoOrder[a.tipo] ?? 9) - (tipoOrder[b.tipo] ?? 9) || (b.isPremium ? 1 : 0) - (a.isPremium ? 1 : 0)
  );
}

export { GOVERNANCE_PRIMARY_SLUGS };
